import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { istDate } from "@/components/DispatchDatePicker";
import { KIND_LABEL } from "@/lib/payoutExport";
import { Field, Modal, SaveBtn, useSubmit } from "./PayoutForms";
import { fmtD, inr } from "./PayoutBits";

const inp = "w-full border border-[#DCDCD3] rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#385C4B]";
const monthStart = () => `${istDate(0).slice(0, 8)}01`;
const signed = (v) => (v.dir === "lena" ? -1 : 1) * (+v.amount || 0);
const fromOb = (ob) => ({ amount: ob ? String(Math.abs(ob.amount)) : "", dir: ob && ob.amount < 0 ? "lena" : "dena" });
const HINT = "Entries before this date are not counted. Everything from this date onward is added automatically.";

export function DirToggle({ value, onChange, compact, testid }) {
  const opts = [["dena", compact ? "Payable" : "Payable (you owe them)"], ["lena", compact ? "Receivable" : "Receivable (they owe you)"]];
  return (
    <div className="flex gap-1" data-testid={testid}>
      {opts.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${value === k ? (k === "lena" ? "bg-[#C8553D] border-[#C8553D] text-white" : "bg-[#385C4B] border-[#385C4B] text-white") : "border-[#DCDCD3] text-[#3D3D36] hover:bg-[#F0F0EA]"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function OpeningForm({ person, ob, onClose, onSaved, onDelete }) {
  const [f, setF] = useState({ date: ob?.date || monthStart(), note: ob?.note || "", ...fromOb(ob) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSubmit(async () => {
    if (f.amount === "") throw new Error("Enter an amount (0 is allowed)");
    await api.put(`/payouts/people/${person.id}/opening`, { date: f.date, amount: signed(f), note: f.note });
    toast.success("Opening balance saved");
  }, onSaved);
  return (
    <Modal title={`${person.name} · Opening Balance`} onClose={onClose} testid="payout-opening-form">
      <div className="grid grid-cols-2 gap-2">
        <Field label="From date"><input type="date" value={f.date} max={istDate(0)} onChange={set("date")} className={inp} data-testid="payout-opening-date" /></Field>
        <Field label="Amount ₹"><input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} className={inp} data-testid="payout-opening-amount-input" /></Field>
      </div>
      <DirToggle value={f.dir} onChange={(dir) => setF({ ...f, dir })} testid="payout-opening-dir" />
      <Field label="Note"><input value={f.note} onChange={set("note")} className={inp} data-testid="payout-opening-note" /></Field>
      <div className="text-[11px] text-[#6B6B61]">{HINT}</div>
      <SaveBtn busy={busy} onClick={save} testid="payout-opening-save" />
      {ob && (
        <button onClick={onDelete} className="w-full text-xs text-[#C8553D] flex items-center justify-center gap-1 py-1.5 rounded hover:bg-red-50" data-testid="payout-opening-remove">
          <Trash2 className="w-3.5 h-3.5" />Remove opening balance
        </button>
      )}
    </Modal>
  );
}

function BulkRow({ p, v, onChange }) {
  const ob = p.opening_balance;
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 min-w-0" data-testid={`payout-bulk-row-${p.id}`}>
      <div className="flex-1 min-w-[140px]">
        <div className="text-sm font-semibold truncate">{p.name}</div>
        <div className="text-[10px] text-[#6B6B61]">{ob ? `Current: ${inr(ob.amount)} · from ${fmtD(ob.date)}` : "No opening set"}</div>
      </div>
      <input type="number" min="0" step="0.01" value={v.amount} placeholder="₹" onChange={(e) => onChange({ ...v, amount: e.target.value })}
        className="w-28 border border-[#DCDCD3] rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#385C4B]" data-testid={`payout-bulk-amount-${p.id}`} />
      <DirToggle compact value={v.dir} onChange={(dir) => onChange({ ...v, dir })} testid={`payout-bulk-dir-${p.id}`} />
    </div>
  );
}

export function BulkOpeningForm({ kind, people, onClose, onSaved }) {
  const [date, setDate] = useState(people.find((p) => p.opening_balance)?.opening_balance.date || monthStart());
  const [vals, setVals] = useState(() => Object.fromEntries(people.map((p) => [p.id, fromOb(p.opening_balance)])));
  const [touched, setTouched] = useState({});
  const [busy, save] = useSubmit(async () => {
    const items = people.filter((p) => touched[p.id] && vals[p.id].amount !== "");
    if (!items.length) throw new Error("Enter or change at least one amount");
    await Promise.all(items.map((p) => api.put(`/payouts/people/${p.id}/opening`, { date, amount: signed(vals[p.id]), note: p.opening_balance?.note || "" })));
    toast.success(`Opening balance saved for ${items.length} ${KIND_LABEL[kind]}${items.length > 1 ? "s" : ""}`);
  }, onSaved);
  return (
    <Modal title={`${KIND_LABEL[kind]} · Opening Balance (All)`} onClose={onClose} size="max-w-2xl" testid="payout-bulk-opening-form">
      <Field label="Date for everyone (auto calculation from this date)">
        <input type="date" value={date} max={istDate(0)} onChange={(e) => e.target.value && setDate(e.target.value)} className={inp} data-testid="payout-bulk-date" />
      </Field>
      <div className="text-[11px] text-[#6B6B61]">{HINT} Only the rows you enter or change are saved (with this date).</div>
      <div className="max-h-[50vh] overflow-y-auto border border-[#EDEDE5] rounded divide-y divide-[#F0F0EA]">
        {people.map((p) => <BulkRow key={p.id} p={p} v={vals[p.id]} onChange={(v) => { setVals({ ...vals, [p.id]: v }); setTouched({ ...touched, [p.id]: true }); }} />)}
      </div>
      <SaveBtn busy={busy} onClick={save} label="Save All" testid="payout-bulk-save" />
    </Modal>
  );
}
